import React, { useEffect, useState } from "react";
import { MsalProvider } from "@azure/msal-react";
import { AccountInfo, AuthenticationResult, EventType } from "@azure/msal-browser";
import { MSAL_ALL_SCOPES, msalInstance } from "./msalConfig";
import { useAuthStore } from "@store/AuthStore";
import { useShallow } from "zustand/react/shallow";
import {pickBestAccount} from "@msal/msalUtils";

async function hydrateFromResult(res?: AuthenticationResult | null) {

    console.log("[MSAL][hydrateFromResult]", res);

    const { setAuth } = useAuthStore.getState();
    if (!res?.account) return;

    console.log("[MSAL][hydrateFromResult] account", res.account.homeAccountId);
    msalInstance.setActiveAccount(res.account);

    setAuth({
        isAuthenticated: true,
        account: res.account,
        ...(res.idTokenClaims ? { idTokenClaims: res.idTokenClaims as any } : {}),
        ...(res.accessToken ? { accessToken: res.accessToken } : {}),
    });
}

async function hydrateFromAccount(account?: AccountInfo | null) {
    console.log("[MSAL][hydrateFromAccount]", account);

    const { setAuth, reset } = useAuthStore.getState();

    if (!account) {
        console.log("[MSAL][hydrateFromAccount] no account -> reset");
        return reset();
    }

    console.log("[MSAL][hydrateFromAccount] account", account.homeAccountId);
    msalInstance.setActiveAccount(account);

    setAuth({
        isAuthenticated: true,
        account,
        ...(account.idTokenClaims ? { idTokenClaims: account.idTokenClaims as any } : {}),
    });

    try {
        console.log("[MSAL][hydrateFromAccount] acquireTokenSilent...");
        const res = await msalInstance.acquireTokenSilent({ account, scopes: MSAL_ALL_SCOPES });
        setAuth({
            accessToken: res.accessToken ?? null,
            ...(res.idTokenClaims ? { idTokenClaims: res.idTokenClaims as any } : {}),
        });
        console.log("[MSAL][hydrateFromAccount] acquireTokenSilent ok");
    } catch (e) {
        console.log("[MSAL][hydrateFromAccount] acquireTokenSilent failed", e);
    }
}

export default function MsalBootstrap({ children }: { children: React.ReactNode }) {
    const [ready, setReady] = useState(false);
    const { setAuth, reset, isAuthenticated } = useAuthStore(
        useShallow((s) => ({ setAuth: s.setAuth, reset: s.reset, isAuthenticated: s.isAuthenticated }))
    );

    useEffect(() => {
        let subId: string | null = null;
        let mounted = true;

        (async () => {
            console.log("[MSAL][bootstrap] initialize...");
            await msalInstance.initialize();
            console.log("[MSAL][bootstrap] initialize ok");

            subId = msalInstance.addEventCallback(async (msg) => {
                if (msg.eventType === EventType.LOGIN_SUCCESS && msg.payload) {
                    console.log("[MSAL][event] LOGIN_SUCCESS");
                    await hydrateFromResult(msg.payload as AuthenticationResult);
                }

                if (msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS && msg.payload) {
                    console.log("[MSAL][event] ACQUIRE_TOKEN_SUCCESS");
                    const res = msg.payload as AuthenticationResult;
                    setAuth({
                        accessToken: (res as any).accessToken ?? null,
                        ...(res.idTokenClaims ? { idTokenClaims: res.idTokenClaims as any } : {}),
                    });
                }

                if (msg.eventType === EventType.LOGOUT_SUCCESS) {
                    console.log("[MSAL][event] LOGOUT_SUCCESS");
                    reset();
                }
            });

            console.log("[MSAL][bootstrap] handleRedirectPromise...");
            const redirectRes = await msalInstance.handleRedirectPromise().catch((e) => {
                console.log("[MSAL][bootstrap] handleRedirectPromise failed", e);
                return null;
            });
            console.log("[MSAL][bootstrap] handleRedirectPromise done", !!redirectRes);

            await hydrateFromResult(redirectRes as any);

            const active = msalInstance.getActiveAccount();

            if (active) {
                console.log("[MSAL][bootstrap] activeAccount already set -> hydrate");
                await hydrateFromAccount(active);
            } else {
                console.log("[MSAL][bootstrap] no activeAccount, picking from cache...");
                const best = pickBestAccount();
                await hydrateFromAccount(best);
            }

            if (mounted) {
                setAuth({ loading: false, overlayVisible: false });
                setReady(true);
                console.log("[MSAL][bootstrap] ready");
            }
        })();

        return () => {
            mounted = false;
            if (subId) msalInstance.removeEventCallback(subId);
        };
    }, [reset, setAuth]);

    console.log("[MSAL][bootstrap] isAuthenticated:", isAuthenticated)

    if (!ready) return null;

    return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
}
